import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminPortal } from '../context/AdminPortalContext';
import { authService } from '../services/authService';

const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatLoginError = (error: any): string => {
  const retryAfter = Number(error?.payload?.retryAfter ?? error?.retryAfter);
  if (error?.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
    return error?.message || `Muitas tentativas de login. Tente novamente em ${retryAfter}s.`;
  }

  return error?.message || 'Erro ao entrar no portal admin';
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSession } = useAdminPortal();
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const redirectTo = useMemo(() => {
    const state = location.state as { from?: { pathname?: string } } | null;
    return state?.from?.pathname || '/overview';
  }, [location.state]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cleanCpf = cpf.replace(/\D/g, '');
      if (cleanCpf.length !== 11) {
        throw new Error('CPF invalido');
      }
      if (!password) {
        throw new Error('Senha obrigatoria');
      }

      await authService.login(cleanCpf, password);
      await refreshSession();
      navigate(redirectTo, { replace: true });
    } catch (nextError: any) {
      setError(formatLoginError(nextError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-shell">
        <section className="admin-login-brand">
          <p className="admin-section-eyebrow">3BET Platform</p>
          <img
            src="/icones/icone-sem-fundo-com-trangulo.png"
            alt="3BET"
            className="admin-login-brand__logo"
          />
          <h1>Portal admin do SaaS</h1>
          <p>
            Controle global da plataforma, owners, jogadores, billing, risco, eventos
            operacionais e saude do sistema.
          </p>
        </section>

        <section className="admin-login-card">
          <div className="admin-login-card__header">
            <p className="admin-section-eyebrow">Acesso restrito</p>
            <h2>Entrar no command center</h2>
          </div>

          <form className="admin-login-form" onSubmit={handleSubmit}>
            <label>
              <span>CPF</span>
              <input
                type="text"
                value={cpf}
                onChange={(event) => setCpf(formatCpf(event.target.value))}
                placeholder="000.000.000-00"
                autoComplete="username"
                disabled={loading}
              />
            </label>

            <label>
              <span>Senha</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Sua senha"
                autoComplete="current-password"
                disabled={loading}
              />
            </label>

            {error ? (
              <div className="admin-banner admin-banner--error" role="alert" aria-live="polite">
                {error}
              </div>
            ) : null}

            <button type="submit" className="admin-primary-button" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
